import { Progress } from '@heroui/react';
import { useEffect, useState } from 'react';
import Button from '@atoms/new/button';
import useLoadingMessages from '@components/Features/hooks/loaderMessages';
import { WarningCircleIcon as ErrorIcon } from '@phosphor-icons/react';

interface ImageProps {
  imageUrl: string;
  isPending: boolean;
  showImage: boolean;
  error: string | null;
  onClose?: () => void;
  onRetry?: () => void;
}

export default function GeneratedImage({
  imageUrl,
  isPending,
  showImage,
  error,
  onClose = () => { },
  onRetry = () => { }
}: ImageProps) {
  const [progress, setProgress] = useState(0);
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);
  const isError = !!error;

  useEffect(() => {
    if (!isPending) return;
    if (!showImage) return;

    const interval = setInterval(() => {
      setProgress((progress) => progress + 10);
    }, 1000);

    setProgressInterval(interval);

    return () => {
      clearInterval(interval);
      setProgressInterval(null);
    };
  }, [isPending]);

  useEffect(() => {
    if (progress < 100) return;
    if (!progressInterval) return;

    clearInterval(progressInterval);
    setProgressInterval(null);
  }, [progress]);

  useEffect(() => {
    if (!isError) return;

    if (!progressInterval) return;

    clearInterval(progressInterval);
    setProgressInterval(null);
  }, [isError]);

  const message = useLoadingMessages(isPending, 1100);

  if (isPending) {
    return (
      <div className='flex items-center justify-center h-full w-full'>
        <div className='py-20 px-[96px] flex flex-col items-center justify-center gap-4 bg-content2 rounded-lg w-[512px] h-[268px]'>
          <Progress
            classNames={{
              base: 'color-[#FF3383]'
            }}
            aria-label="Loading..."
            isStriped
            value={progress}
          />
          <p className='text-content2-foreground text-xl font-normal text-center'>{message}</p>
        </div>
      </div>
    );
  }

  if (!showImage) return null;

  return (
    <div className='flex items-center justify-center h-full w-full overflow-hidden'>
      {error && (
        <div className='select-none p-20 dark:bg-grey-900 bg-white rounded-lg flex flex-col items-center justify-center gap-4 max-w-[487px]'>
          <ErrorIcon className='dark:text-white text-grey-900' />
          <p className='dark:text-white text-grey-900 text-xl font-normal text-center'>
            {error.includes('No comments found') ?
              'No responses yet. Ask participants to type in the chat and try again.' :
              'There\'s an issue with image generation feature.'
            }
          </p>

          <Button
            kind='secondary'
            onPress={() => {
              if (error.includes('No comments found')) {
                onClose();
                return;
              }

              return onRetry();
            }}
          >
            {error?.includes('No comments found') ? 'Close' : 'Try again'}
          </Button>
        </div>
      )}
      {imageUrl && (
        <div className="relative w-full h-full">
          <img src={`${process.env.NEXT_PUBLIC_API_URL}image-proxy?url=${encodeURIComponent(imageUrl)}`} alt="Generated" className="rounded-xl w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}
